import { useCallback, useEffect, useRef, useState } from 'react'

import { getMicStream, holdMic, releaseMic, setMicRecording } from '../lib/micStream'
import { reusableContext } from '../lib/audioGraph'
import { LEVEL_WINDOW_MS, levelOf, pushLevel } from '../lib/practice'
import { concatFloat32, encodeWav16kMono, resampleTo16kHz } from '../lib/wav'

const WORKLET_URL = '/worklets/pcm-recorder-processor.js'
const MAX_SECONDS = 20 // matches the Android app's cap and the backend's expectation

/**
 * Microphone recording with a reviewable result.
 *
 * Records real WAV PCM via the Web Audio API rather than `MediaRecorder`:
 * the assessment endpoint only accepts WAV (16kHz mono) or true OGG/Opus,
 * and MediaRecorder cannot produce either in Chrome (WebM/Opus only) or
 * Safari (MP4/AAC only) — see src/lib/wav.js for why.
 *
 * The recording is deliberately *not* submitted automatically when it stops.
 * The learner listens back and decides, which is one of the acceptance
 * criteria and also the difference between a tool people trust with their
 * voice and one they do not.
 */
export function useAudio() {
  const [state, setState] = useState('idle') // idle | requesting | ready | recording | recorded
  const [error, setError] = useState(null)
  const [errorCode, setErrorCode] = useState(null) // 'denied' | 'unavailable' | null
  const [seconds, setSeconds] = useState(0)
  const [blob, setBlob] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  // The live strip (P-core-2): one loudness level per window, newest last.
  const [levels, setLevels] = useState([])
  // The take as 16 kHz mono samples, and its length in whole seconds, once stopped.
  const [samples, setSamples] = useState(null)
  const [recordedSeconds, setRecordedSeconds] = useState(0)

  const streamRef = useRef(null)
  const audioContextRef = useRef(null)
  const workletReadyRef = useRef(false)
  const sourceNodeRef = useRef(null)
  const workletNodeRef = useRef(null)
  const chunksRef = useRef([])
  const timerRef = useRef(null)
  const meterRef = useRef({ sum: 0, count: 0, stamp: 0, levels: [] })
  // R13: who else hears the raw quanta while recording — the live-text
  // recognizer, fed at the microphone's own rate. Null when nobody listens.
  const chunkListenerRef = useRef(null)

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  // The stream itself belongs to lib/micStream.js, shared by every lesson
  // page (owner, 2026-10-14: the microphone is asked for once). Letting go
  // here only drops this page's reference; the module stops it once nobody
  // holds it, or when the tab is hidden.
  const releaseStream = useCallback(() => {
    streamRef.current = null
  }, [])

  const disconnectGraph = useCallback(() => {
    sourceNodeRef.current?.disconnect()
    workletNodeRef.current?.disconnect()
    sourceNodeRef.current = null
    workletNodeRef.current = null
  }, [])

  /** Ask for the microphone. Separated from start() so the browser prompt
   *  appears when the learner opens the exercise, not mid-countdown. The
   *  stream is the app's one (lib/micStream.js): open already when another
   *  lesson used it a moment ago, and asked for again only when it is not. */
  const requestMicrophone = useCallback(async () => {
    if (streamRef.current?.getAudioTracks().some((track) => track.readyState === 'live')) return true
    setState('requesting')
    setError(null)
    setErrorCode(null)
    try {
      streamRef.current = await getMicStream()
      setState('ready')
      return true
    } catch (err) {
      setState('idle')
      const denied = err?.name === 'NotAllowedError'
      setErrorCode(denied ? 'denied' : 'unavailable')
      setError(
        denied
          ? 'دسترسی به میکروفون داده نشد. از تنظیمات مرورگر اجازه دهید.'
          : 'میکروفونی پیدا نشد.',
      )
      return false
    }
  }, [])

  const stop = useCallback(() => {
    stopTimer()
    if (workletNodeRef.current) {
      const nativeRate = audioContextRef.current.sampleRate
      const raw = concatFloat32(chunksRef.current)
      const resampled = resampleTo16kHz(raw, nativeRate)
      setSamples(resampled)
      // Whole seconds, rounded up — the same rounding the server applies to the upload.
      setRecordedSeconds(Math.ceil(resampled.length / 16000))
      const wavBuffer = encodeWav16kMono(resampled)
      const recorded = new Blob([wavBuffer], { type: 'audio/wav' })

      disconnectGraph()
      setMicRecording(false)
      setBlob(recorded)
      setPreviewUrl(URL.createObjectURL(recorded))
      setState('recorded')
    }
  }, [disconnectGraph, stopTimer])

  const start = useCallback(async () => {
    const ok = await requestMicrophone()
    if (!ok) return false

    // A closed context can make no nodes: its worklet scope is gone. Never
    // reuse one — a second take on the same page would fail silently on it.
    if (!reusableContext(audioContextRef.current)) {
      audioContextRef.current = null
      workletReadyRef.current = false
    }
    const audioContext = audioContextRef.current ?? new AudioContext()
    audioContextRef.current = audioContext
    if (audioContext.state === 'suspended') await audioContext.resume()

    if (!workletReadyRef.current) {
      await audioContext.audioWorklet.addModule(WORKLET_URL)
      workletReadyRef.current = true
    }

    chunksRef.current = []
    const source = audioContext.createMediaStreamSource(streamRef.current)
    const worklet = new AudioWorkletNode(audioContext, 'pcm-recorder-processor', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
    })
    const meter = meterRef.current
    meter.sum = 0
    meter.count = 0
    meter.stamp = performance.now()
    meter.levels = []
    setLevels([])
    setSamples(null)
    setRecordedSeconds(0)
    worklet.port.onmessage = (event) => {
      const chunk = event.data
      chunksRef.current.push(chunk)
      chunkListenerRef.current?.(chunk, audioContext.sampleRate)
      // One level per LEVEL_WINDOW_MS, not per quantum: 128 frames land every ~3 ms,
      // and a state update at that rate would fight the recording for the main thread.
      for (let i = 0; i < chunk.length; i++) meter.sum += chunk[i] * chunk[i]
      meter.count += chunk.length
      const now = performance.now()
      if (now - meter.stamp >= LEVEL_WINDOW_MS && meter.count > 0) {
        meter.levels = pushLevel(meter.levels, levelOf(Math.sqrt(meter.sum / meter.count)))
        setLevels(meter.levels)
        meter.sum = 0
        meter.count = 0
        meter.stamp = now
      }
    }
    source.connect(worklet)
    // The worklet writes no output of its own (silence), but Chrome only
    // keeps a node's process() running while it's part of a live graph
    // reaching the destination.
    worklet.connect(audioContext.destination)

    sourceNodeRef.current = source
    workletNodeRef.current = worklet

    setSeconds(0)
    setMicRecording(true)
    setState('recording')
    timerRef.current = setInterval(() => {
      setSeconds((value) => {
        const next = value + 1
        if (next >= MAX_SECONDS) stop()
        return next
      })
    }, 1000)
    return true
  }, [requestMicrophone, stop])

  /**
   * Take an already-made recording back into the review (V9-w): the take
   * the outbox kept across a reload, handed back so the learner finds the
   * review where they left it instead of an empty microphone.
   *
   * Refuses while a take is live in this session — a restore is only ever
   * for a screen that has none — so a slow database read cannot land on top
   * of something the learner has just recorded. `samples` stay null: only
   * the encoded WAV was kept, not the Float32 array, so a restored take
   * draws a flat strip (`takeEnvelope` already handles that) and its length
   * comes from the stored record rather than being measured again.
   */
  const adopt = useCallback((recording, storedSeconds = 0) => {
    if (!recording || blob || state === 'recording') return false
    setBlob(recording)
    setPreviewUrl(URL.createObjectURL(recording))
    setSamples(null)
    setLevels([])
    setSeconds(storedSeconds)
    setRecordedSeconds(storedSeconds)
    setState('recorded')
    return true
  }, [blob, state])

  const discard = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setBlob(null)
    setPreviewUrl(null)
    setSeconds(0)
    setLevels([])
    setSamples(null)
    setRecordedSeconds(0)
    setState(streamRef.current ? 'ready' : 'idle')
  }, [previewUrl])

  // This page holds the app's microphone while it is open, so a take does
  // not ask for it again (lib/micStream.js); leaving lets it go after a
  // short grace, for the next lesson to reuse.
  useEffect(() => {
    holdMic()
    return () => {
      setMicRecording(false)
      releaseMic()
    }
  }, [])

  // A take replaced by the next one (or discarded) lets go of its object URL.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  // Release this page's audio graph and context when the exercise unmounts —
  // only then: a running AudioContext keeps consuming CPU (the microphone
  // itself is lib/micStream.js's, held above). This used to share an effect
  // with the object URL above, so it also ran after every take: the context
  // was closed under the page, and the next take (a re-record, R13's second
  // repetition) failed on it.
  useEffect(() => {
    return () => {
      stopTimer()
      disconnectGraph()
      releaseStream()
      const context = audioContextRef.current
      audioContextRef.current = null
      workletReadyRef.current = false
      if (context && context.state !== 'closed') void context.close()
    }
  }, [disconnectGraph, releaseStream, stopTimer])

  /** R13: hand every recorded quantum to `listener(chunk, sampleRate)` as well; null to stop. */
  const setChunkListener = useCallback((listener) => {
    chunkListenerRef.current = listener
  }, [])

  /** The practice's AudioContext once the microphone has been started — the ding plays on it (R13). */
  const context = useCallback(() => audioContextRef.current, [])

  return {
    state,
    error,
    errorCode,
    seconds,
    blob,
    previewUrl,
    levels,
    samples,
    recordedSeconds,
    filename: 'recording.wav',
    isRecording: state === 'recording',
    hasRecording: state === 'recorded',
    requestMicrophone,
    start,
    stop,
    adopt,
    discard,
    setChunkListener,
    context,
  }
}
